import React, { useState, useEffect, useRef, useCallback } from 'react';

import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

import {
  FaArrowLeft,
  FaShare,
  FaHeart,
  FaRegHeart,
  FaPlus,
  FaCheck,
  FaStar,
  FaSearch,
  FaArrowUp,
  FaArrowDown,
  FaSpinner,
  FaTicketAlt,
  FaStamp,
  FaUtensils,
} from 'react-icons/fa';
import { useAuth } from '../auth/context/AuthContext';
import useCustomerAuthCheck from '../auth/hooks/useCustomerAuthCheck';
import LoginDialog from './LoginDialog';
import Pagination from '../restaurants/Pagination'; // Import the Pagination component

import api from '../../services/api';

const RestaurantDetailsPage = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  // const { isCustomerAuthenticated, customerToken } = useAuth();
  // const {
  //   isTokenValid,
  //   showLoginDialog,
  //   setShowLoginDialog,
  //   handleAuthenticatedAction,
  // } = useCustomerAuthCheck();
  const { isCustomerAuthenticated, customerToken, customerLogin } = useAuth();
  const {
    isTokenValid,
    showLoginDialog,
    setShowLoginDialog,
    handleAuthenticatedAction,
  } = useCustomerAuthCheck();

  const [restaurant, setRestaurant] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showListDialog, setShowListDialog] = useState(false);
  const [lists, setLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [newListName, setNewListName] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  // const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [restaurantLists, setRestaurantLists] = useState([]);

  const [coupons, setCoupons] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New state variables for search, pagination, and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    currentPage: 1,
    couponsPerPage: 6,
    searchTerm: '',
    sortField: 'expirationDate',
    sortDirection: 'asc',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCoupons: 0,
  });

  const [customerStamps, setCustomerStamps] = useState(0);
  const [stampRewards, setStampRewards] = useState([]);

  const [isAddingToList, setIsAddingToList] = useState(false);

  const [loadingStamps, setLoadingStamps] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(false);

  const dialogRef = useRef(null);

  const handleLogin = async (token) => {
    customerLogin(token);
    // axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // await new Promise((resolve) => setTimeout(resolve, 100));
    if (isCustomerAuthenticated && isTokenValid && customerToken) {
      fetchSubscriptionStatus();
      fetchFavoriteStatus();
      fetchLists();
      fetchCoupons();
      fetchStampRewards();
    }
  };

  // const fetchCoupons = async () => {
  //   try {
  //     const response = await axios.get(
  //       `/api/restaurants/customers/coupons/${restaurantId}`,
  //       {
  //         headers: { Authorization: `Bearer ${customerToken}` },
  //       }
  //     );
  //     console.log(response.data);
  //     setCoupons(response.data);
  //   } catch (error) {
  //     console.error('Error fetching coupons:', error);
  //     toast.error('Failed to fetch coupons');
  //   }
  // };
  const fetchCustomerStamps = useCallback(async () => {
    setLoadingStamps(true);

    try {
      const response = await api.get(
        `/restaurants/customers/stamps/${restaurantId}`,
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      setCustomerStamps(response.data.count);
    } catch (error) {
      console.error('Error fetching customer stamps:', error);
      toast.error('Failed to fetch stamps');
    } finally {
      setLoadingStamps(false);
    }
  }, [restaurantId, customerToken]);
  const fetchStampRewards = useCallback(async () => {
    setLoadingStamps(true);

    try {
      const response = await api.get(
        `/restaurants/${restaurantId}/customers/stamp-rewards`,
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      setStampRewards(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching stamp rewards:', error);
      toast.error('Failed to fetch stamp rewards');
    } finally {
      setLoadingStamps(false);
    }
  }, [restaurantId, customerToken]);

  const handleRedeemReward = async (rewardId) => {
    try {
      const response = await api.post(
        `/restaurants/customers/redeem-stamps`,
        { rewardId, restaurantId },
        { headers: { Authorization: `Bearer ${customerToken}` } }
      );
      toast.success('Reward redeemed successfully!');
      fetchCustomerStamps(); // Refresh stamps count
      // You might want to show a modal or navigate to a page showing the coupon details
      console.log('Coupon received:', response.data.coupon);
    } catch (error) {
      console.error('Error redeeming reward:', error);
      toast.error('Failed to redeem reward');
    }
  };

  const fetchCoupons = useCallback(async () => {
    if (!isCustomerAuthenticated || !isTokenValid || !customerToken) return;

    try {
      setLoadingCoupons(true);
      const response = await api.get(
        `/restaurants/customers/coupons/${restaurantId}`,
        {
          headers: { Authorization: `Bearer ${customerToken}` },
          params: {
            page: filters.currentPage,
            limit: filters.couponsPerPage,
            search: filters.searchTerm,
            sortBy: filters.sortField,
            sortOrder: filters.sortDirection,
          },
        }
      );
      setCoupons(response.data.coupons);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalCoupons: response.data.totalCoupons,
      });
      setError(null);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setError('Failed to fetch coupons');
    } finally {
      setLoadingCoupons(false);
    }
  }, [
    restaurantId,
    isCustomerAuthenticated,
    isTokenValid,
    customerToken,
    filters,
  ]);

  // useEffect(() => {
  //   if (activeTab === 'coupons') {
  //     fetchCoupons();
  //   }
  // }, [fetchCoupons, activeTab]);

  // useEffect(() => {
  //   if (activeTab === 'stamps') {
  //     fetchCustomerStamps();
  //   }
  // }, [fetchCustomerStamps, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        setShowListDialog(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (showListDialog) {
      setSelectedLists(restaurantLists);
    }
  }, [showListDialog, restaurantLists]);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await api.get(`/restaurants/${restaurantId}`);
      setRestaurant(response.data);
      setError(null); // Clear any previous errors

      // console.log(response);
    } catch (error) {
      // setError('Failed to load restaurant details.');
      if (error.response && error.response.status === 404) {
        setError('Restaurant not found');
      } else {
        setError('Failed to fetch restaurant details');
        toast.error('Failed to fetch restaurant details');
      }
      setRestaurant(null); // Clear the restaurant data
      // toast.error('Failed to fetch restaurant details');
    } finally {
      setLoading(false); // Ensure loading is set to false regardless of the outcome
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get(
        `/customers/subscription/${restaurantId}`,
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      setIsSubscribed(response.data.isSubscribed);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const fetchFavoriteStatus = async () => {
    try {
      const response = await api.get(`/customers/favorite/${restaurantId}`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      setIsFavorite(response.data.isFavorite);
    } catch (error) {
      console.error('Error fetching favorite status:', error);
    }
  };

  // const fetchLists = async () => {
  //   try {
  //     const response = await api.get('/customers/lists', {
  //       headers: { Authorization: `Bearer ${customerToken}` },
  //     });
  //     setLists(response.data);
  //   } catch (error) {
  //     console.error('Error fetching lists:', error);
  //   }
  // };
  const fetchLists = async () => {
    const token = customerToken || localStorage.getItem('customerToken');
    if (!token) {
      toast.error('You are not authenticated');
      return;
    }

    try {
      const [listsResponse, restaurantListsResponse] = await Promise.all([
        api.get('/customers/lists', {
          headers: { Authorization: `Bearer ${customerToken}` },
        }),
        api.get(`/customers/lists/restaurant/${restaurantId}`, {
          headers: { Authorization: `Bearer ${customerToken}` },
        }),
      ]);
      setLists(listsResponse.data);
      setRestaurantLists(restaurantListsResponse.data);
      setSelectedLists(restaurantListsResponse.data);
    } catch (error) {
      console.error('Error fetching lists:', error);
    }
  };

  // const handleSubscribe = async () => {
  //   try {
  //     const response = await api.post(
  //       // `/restaurants/${restaurantId}/subscribe`,
  //       `/customers/subscribe/${restaurantId}`,

  //       {},
  //       { headers: { Authorization: `Bearer ${customerToken}` } }
  //     );
  //     if (response.data.success) {
  //       toast.success('Subscribed success');
  //       setIsSubscribed(true);
  //       setRestaurant((prev) => ({
  //         ...prev,
  //         subscribersCount: prev.subscribersCount + 1,
  //       }));
  //     }
  //   } catch (error) {
  //     // setError('Failed to subscribe. Please try again.');
  //     toast.error('Failed to subscribe. Please try again later');
  //   }
  // };

  // const handleUnsubscribe = async () => {
  //   try {
  //     const response = await api.post(
  //       // `/restaurants/${restaurantId}/subscribe`,
  //       `/customers/unsubscribe/${restaurantId}`,

  //       {},
  //       { headers: { Authorization: `Bearer ${customerToken}` } }
  //     );
  //     console.log(response);
  //     if (response.status == '200') {
  //       setIsSubscribed(false);
  //       setRestaurant((prev) => ({
  //         ...prev,
  //         subscribersCount: prev.subscribersCount - 1,
  //       }));
  //     }
  //     toast.success('Unsubscribed success');
  //   } catch (error) {
  //     // setError('Failed to subscribe. Please try again.');
  //     toast.error('Failed to subscribe. Please try again later');
  //   }
  // };

  // const handleAddToLists = async () => {
  //   try {
  //     for (const listId of selectedLists) {
  //       await api.post(
  //         `/customers/lists/${listId}/restaurants/${restaurantId}`,
  //         {},
  //         { headers: { Authorization: `Bearer ${customerToken}` } }
  //       );
  //     }
  //     setShowListDialog(false);
  //     setSelectedLists([]);
  //     fetchLists(); // Refresh lists after adding

  //     fetchFavoriteStatus(); // Refresh favorite status
  //   } catch (error) {
  //     // setError('Failed to add restaurant to the list(s). Please try again.');
  //     toast.error('Failed to add restaurant to the list(s). Please try again.');
  //   }
  // };

  // const handleCreateNewList = async () => {
  //   try {
  //     const response = await api.post(
  //       '/customers/lists',
  //       { name: newListName, type: 'regular' },
  //       { headers: { Authorization: `Bearer ${customerToken}` } }
  //     );
  //     setLists([...lists, response.data]);
  //     setNewListName('');
  //   } catch (error) {
  //     setError('Failed to create new list. Please try again.');
  //     toast.error('Failed to create new list. Please try again.');
  //   }
  // };

  // const toggleListSelection = (listId) => {
  //   setSelectedLists((prev) =>
  //     prev.includes(listId)
  //       ? prev.filter((id) => id !== listId)
  //       : [...prev, listId]
  //   );
  // };

  const handleSubscribe = async () => {
    setIsProcessing(true);
    try {
      const response = await api.post(
        `/customers/subscribe/${restaurantId}`,
        {},
        { headers: { Authorization: `Bearer ${customerToken}` } }
      );
      if (response.data.success) {
        toast.success('Subscribed successfully');
        setIsSubscribed(true);
        setRestaurant((prev) => ({
          ...prev,
          subscribersCount: prev.subscribersCount + 1,
        }));
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again later');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsProcessing(true);
    try {
      const response = await api.post(
        `/customers/unsubscribe/${restaurantId}`,
        {},
        { headers: { Authorization: `Bearer ${customerToken}` } }
      );
      if (response.status === 200) {
        setIsSubscribed(false);
        setRestaurant((prev) => ({
          ...prev,
          subscribersCount: prev.subscribersCount - 1,
        }));
        toast.success('Unsubscribed successfully');
      }
    } catch (error) {
      toast.error('Failed to unsubscribe. Please try again later');
    } finally {
      setIsProcessing(false);
    }
  };
  // const toggleFavorite = async () => {
  //   try {
  //     if (isFavorite) {
  //       await api.delete(`/customers/favorite/${restaurantId}`, {
  //         headers: { Authorization: `Bearer ${customerToken}` },
  //       });
  //     } else {
  //       await api.post(
  //         `/customers/favorite/${restaurantId}`,
  //         {},
  //         { headers: { Authorization: `Bearer ${customerToken}` } }
  //       );
  //     }
  //     setIsFavorite(!isFavorite);
  //     fetchLists(); // Refresh the lists after toggling favorite
  //     toast.success(
  //       isFavorite ? 'Removed from favorites' : 'Added to favorites'
  //     );
  //   } catch (error) {
  //     toast.error('Failed to update favorite status');
  //   }
  // };
  const toggleFavorite = async () => {
    handleAuthenticatedAction(async () => {
      try {
        if (isFavorite) {
          await api.delete(`/customers/favorite/${restaurantId}`, {
            headers: { Authorization: `Bearer ${customerToken}` },
          });
        } else {
          await api.post(
            `/customers/favorite/${restaurantId}`,
            {},
            { headers: { Authorization: `Bearer ${customerToken}` } }
          );
        }
        setIsFavorite(!isFavorite);
        fetchLists(); // Refresh the lists after toggling favorite
        toast.success(
          isFavorite ? 'Removed from favorites' : 'Added to favorites'
        );
      } catch (error) {
        toast.error('Failed to update favorite status');
      }
    });
  };

  // const handleAddToLists = async () => {
  //   try {
  //     for (const listId of selectedLists) {
  //       await api.post(
  //         `/customers/lists/${listId}/restaurants/${restaurantId}`,
  //         {},
  //         { headers: { Authorization: `Bearer ${customerToken}` } }
  //       );
  //     }
  //     setShowListDialog(false);
  //     setSelectedLists([]);
  //     fetchLists();
  //     toast.success('Added to selected lists');
  //   } catch (error) {
  //     toast.error('Failed to add restaurant to the list(s). Please try again.');
  //   }
  // };
  // const handleAddToLists = async () => {
  //   try {
  //     for (const listId of lists.map((list) => list._id)) {
  //       if (selectedLists.includes(listId)) {
  //         await api.post(
  //           `/customers/lists/${listId}/restaurants/${restaurantId}`,
  //           {},
  //           { headers: { Authorization: `Bearer ${customerToken}` } }
  //         );
  //       } else {
  //         await api.delete(
  //           `/customers/lists/${listId}/restaurants/${restaurantId}`,
  //           { headers: { Authorization: `Bearer ${customerToken}` } }
  //         );
  //       }
  //     }
  //     setShowListDialog(false);
  //     fetchLists(); // Refresh lists after updating
  //     toast.success('Lists updated successfully');
  //   } catch (error) {
  //     toast.error('Failed to update lists. Please try again.');
  //   }
  // };

  const handleAddToLists = async () => {
    setIsAddingToList(true);

    try {
      const favoritesList = lists.find((list) => list.type === 'favorites');
      const favoriteListId = favoritesList ? favoritesList._id : null;

      for (const listId of lists.map((list) => list._id)) {
        if (selectedLists.includes(listId)) {
          await api.post(
            `/customers/lists/${listId}/restaurants/${restaurantId}`,
            {},
            { headers: { Authorization: `Bearer ${customerToken}` } }
          );
        } else {
          await api.delete(
            `/customers/lists/${listId}/restaurants/${restaurantId}`,
            { headers: { Authorization: `Bearer ${customerToken}` } }
          );
        }
      }
      setShowListDialog(false);
      fetchLists(); // Refresh lists after updating
      fetchFavoriteStatus(); // Refresh favorite status
      toast.success('Lists updated successfully');
    } catch (error) {
      toast.error('Failed to update lists. Please try again.');
    } finally {
      setIsAddingToList(false);
    }
  };

  // const handleAddToLists = async () => {
  //   try {
  //     for (const listId of lists.map((list) => list._id)) {
  //       if (selectedLists.includes(listId)) {
  //         await api.post(
  //           `/customers/lists/${listId}/restaurants/${restaurantId}`,
  //           {},
  //           { headers: { Authorization: `Bearer ${customerToken}` } }
  //         );
  //       } else {
  //         await api.delete(
  //           `/customers/lists/${listId}/restaurants/${restaurantId}`,
  //           { headers: { Authorization: `Bearer ${customerToken}` } }
  //         );
  //       }
  //     }
  //     setShowListDialog(false);
  //     fetchLists(); // Refresh lists after updating
  //     toast.success('Lists updated successfully');
  //   } catch (error) {
  //     toast.error('Failed to update lists. Please try again.');
  //   }
  // };

  const handleCreateNewList = async () => {
    try {
      const response = await api.post(
        '/customers/lists',
        { name: newListName, type: 'regular' },
        { headers: { Authorization: `Bearer ${customerToken}` } }
      );
      setLists([...lists, response.data]);
      setNewListName('');
    } catch (error) {
      toast.error('Failed to create new list. Please try again.');
    }
  };

  // const toggleListSelection = (listId) => {
  //   setSelectedLists((prev) =>
  //     prev.includes(listId)
  //       ? prev.filter((id) => id !== listId)
  //       : [...prev, listId]
  //   );
  // };
  const toggleListSelection = (listId) => {
    setSelectedLists((prev) => {
      if (prev.includes(listId)) {
        return prev.filter((id) => id !== listId);
      } else {
        return [...prev, listId];
      }
    });
  };
  const handleSearchChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      searchTerm: e.target.value,
      currentPage: 1,
    }));
  };

  const toggleExpirationFilter = () => {
    setFilters((prev) => ({
      ...prev,
      expirationFilter:
        prev.expirationFilter === 'not_expired' ? 'all' : 'not_expired',
      currentPage: 1,
    }));
  };

  const handleSortChange = (field) => {
    setFilters((prev) => ({
      ...prev,
      sortField: field,
      sortDirection:
        prev.sortField === field && prev.sortDirection === 'asc'
          ? 'desc'
          : 'asc',
      currentPage: 1,
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleCouponsPerPageChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      couponsPerPage: Number(e.target.value),
      currentPage: 1,
    }));
  };

  // useEffect(() => {
  //   fetchRestaurantDetails();
  //   // if (isCustomerAuthenticated && isTokenValid && customerToken) {
  //   //   fetchSubscriptionStatus();
  //   //   fetchFavoriteStatus();
  //   //   fetchLists();
  //   // }
  //   if (!isCustomerAuthenticated || !customerToken || !isTokenValid) {
  //     // If the user is not authenticated or the token is missing, reset the relevant state
  //     setIsSubscribed(false);
  //     setIsFavorite(false);
  //     setLists([]);
  //     setRestaurantLists([]);
  //   } else {
  //     // Fetch the subscription, favorite status, and lists if authenticated
  //     fetchSubscriptionStatus();
  //     fetchFavoriteStatus();
  //     fetchLists();

  //     if (activeTab === 'coupons') {
  //       fetchCoupons();
  //     } else if (activeTab === 'stamps') {
  //       fetchCustomerStamps();

  //       fetchStampRewards();
  //     }
  //   }
  //   // }, [restaurantId, isCustomerAuthenticated]);
  // }, [
  //   isCustomerAuthenticated,
  //   isTokenValid,
  //   customerToken,
  //   activeTab,
  //   restaurantId,
  //   fetchCoupons,
  //   fetchCustomerStamps,
  //   fetchStampRewards,
  //   error,
  //   loading,
  // ]);

  // Effect for fetching restaurant details and authentication-dependent data
  useEffect(() => {
    fetchRestaurantDetails();

    if (isCustomerAuthenticated && isTokenValid && customerToken) {
      fetchSubscriptionStatus();
      fetchFavoriteStatus();
      fetchLists();
    } else {
      setIsSubscribed(false);
      setIsFavorite(false);
      setLists([]);
      setRestaurantLists([]);
    }
  }, [isCustomerAuthenticated, isTokenValid, customerToken, restaurantId]);

  // Separate effect for handling tab-specific data
  useEffect(() => {
    if (isCustomerAuthenticated && isTokenValid && customerToken) {
      if (activeTab === 'coupons') {
        fetchCoupons();
      } else if (activeTab === 'stamps') {
        fetchCustomerStamps();
        fetchStampRewards();
      }
    }
  }, [
    activeTab,
    isCustomerAuthenticated,
    isTokenValid,
    customerToken,
    fetchCoupons,
    fetchCustomerStamps,
    fetchStampRewards,
  ]);

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='w-16 h-16 border-t-4 border-blue-500 border-solid rounded-full animate-spin'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='text-center py-10'>
        <FaUtensils className='text-6xl text-gray-300 mx-auto mb-4' />
        <p className='text-xl text-gray-500'>{error}</p>
        <p className='text-gray-400 mt-2'>
          This restaurant may have been removed or is no longer available.
        </p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='w-16 h-16 border-t-4 border-blue-500 border-solid rounded-full animate-spin'></div>
      </div>
    );
  }

  return (
    <div className='relative h-screen'>
      <div className='h-1/3 bg-gradient-to-r from-pink-500 to-orange-500'>
        {restaurant.profileImage && (
          <img
            src={restaurant.profileImage}
            alt={restaurant.name}
            className='w-full h-full object-cover'
          />
        )}
      </div>
      {/* <div className='absolute top-4 left-4'>
        <button onClick={() => navigate(-1)} className='text-white'>
          <FaArrowLeft size={24} />
        </button>
      </div>
      <div className='absolute top-4 right-4'>
        <button className='text-white'>
          <FaShare size={24} />
        </button>
      </div> */}

      <div className='bg-white rounded-3xl -mt-8 p-6 relative max-w-[800px] mx-auto'>
        <div className='absolute top-[-70px] left-0 right-0 text-white py-2 px-4'>
          <div className='flex justify-between'>
            <span className='mr-4 bg-black bg-opacity-50 p-4 rounded-md'>
              {restaurant.subscribersCount} subscribers
            </span>
            {/* <button
              onClick={() => toggleListSelection('favorites')}
              // onClick={() => setShowListDialog(true)}
              className='mr-4 bg-black bg-opacity-50 p-4 rounded-md'
            >
              {isFavorite ? <FaHeart size={24} /> : <FaRegHeart size={24} />}
            </button> */}
            <button
              onClick={toggleFavorite}
              className='mr-4 bg-black bg-opacity-50 p-4 rounded-md'
            >
              {isFavorite ? (
                <FaHeart size={24} className='text-red-500' />
              ) : (
                <FaRegHeart size={24} className='text-gray-300' />
              )}
            </button>
          </div>
        </div>
        <div className='flex flex-col items-center mb-4 gap-2'>
          <h1 className='text-2xl font-bold'>{restaurant.name}</h1>
          <div className='flex mb-2'>
            <FaStar size={24} className='text-yellow-400' />{' '}
            <span>4.9(921) </span>
          </div>
          <div className='flex gap-2'>
            {/* <button
              onClick={() => setShowListDialog(true)}
              className='bg-blue-500 text-white py-2 px-4 rounded-lg font-bold'
            >
              <FaPlus className='inline mr-2 text-center' />
              Add to list
            </button> */}
            <button
              onClick={() =>
                handleAuthenticatedAction(() => setShowListDialog(true))
              }
              className='bg-blue-500 text-white py-2 px-4 rounded-lg font-bold'
            >
              <FaPlus className='inline mr-2 text-center' />
              Add to list
            </button>

            {/* <>
              {isSubscribed ? (
                <>
                  <button
                    onClick={
                      isCustomerAuthenticated
                        ? handleUnsubscribe
                        : () => navigate('/login')
                    }
                    className='bg-red-500 text-white py-2 px-4 rounded-lg font-bold'
                  >
                    Unsubscribe{' '}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={
                      isCustomerAuthenticated
                        ? handleSubscribe
                        : () => navigate('/login')
                    }
                    className='bg-green-500 text-white py-2 px-4 rounded-lg font-bold'
                  >
                    Subscribe{' '}
                  </button>
                </>
              )}
            </> */}
            <button
              onClick={() =>
                handleAuthenticatedAction(
                  isSubscribed ? handleUnsubscribe : handleSubscribe
                )
              }
              disabled={isProcessing}
              className={`bg-${
                isSubscribed ? 'red' : 'green'
              }-500 text-white py-2 px-4 rounded-lg font-bold ${
                isProcessing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {/* <button
              onClick={
                isCustomerAuthenticated
                  ? isSubscribed
                    ? handleUnsubscribe
                    : handleSubscribe
                  : () => navigate('/login')
              }
              disabled={isProcessing}
              className={`bg-${
                isSubscribed ? 'red' : 'green'
              }-500 text-white py-2 px-4 rounded-lg font-bold ${
                isProcessing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            > */}
              {isProcessing
                ? 'Processing...'
                : isSubscribed
                ? 'Unsubscribe'
                : 'Subscribe'}
            </button>
          </div>
        </div>
        {/* <p className='text-gray-600 mb-2'>Categories: {restaurant.category}</p>
        <p className='text-gray-600 mb-2'>Address: {restaurant.address}</p>
        <p className='text-gray-600 mb-2'>
          Phone Number: {restaurant.phoneNumber}
        </p>
        <p className='text-gray-600 mb-4'>Open Hours:</p> */}
        <div className=' bg-white border-t flex justify-around py-2'>
          <button
            onClick={() => setActiveTab('details')}
            className={`font-semibold w-full h-full p-4 ${
              activeTab === 'details'
                ? 'text-blue-500 border-blue-400 border-b-2'
                : 'text-gray-500'
            }`}
          >
            Details
          </button>
          {/* <button
            onClick={() => setActiveTab('coupons')}
            className={`font-semibold w-full ${
              activeTab === 'coupons'
                ? 'text-blue-500 border-blue-400 border-b-2'
                : 'text-gray-500'
            }`}
          >
            My Coupons
          </button>
          <button
            onClick={() => setActiveTab('stamps')}
            className={`font-semibold  w-full ${
              activeTab === 'stamps'
                ? 'text-blue-500 border-blue-400 border-b-2'
                : 'text-gray-500'
            }`}
          >
            Stamps
          </button> */}
          <button
            onClick={() =>
              handleAuthenticatedAction(() => setActiveTab('coupons'))
            }
            className={`font-semibold w-full ${
              activeTab === 'coupons'
                ? 'text-blue-500 border-blue-400 border-b-2'
                : 'text-gray-500'
            }`}
          >
            My Coupons
          </button>
          <button
            onClick={() =>
              handleAuthenticatedAction(() => setActiveTab('stamps'))
            }
            className={`font-semibold w-full ${
              activeTab === 'stamps'
                ? 'text-blue-500 border-blue-400 border-b-2'
                : 'text-gray-500'
            }`}
          >
            Stamps
          </button>
        </div>
        {/* <div className='flex justify-between mt-6 border-t border-b py-4'>
          <button
            onClick={() => setShowListDialog(true)}
            className='text-blue-500 font-semibold'
          >
            <FaPlus className='inline mr-2' />
            Add to list
          </button>
          <button className='text-blue-500 font-semibold'>Get Direction</button>
          <button className='text-blue-500 font-semibold'>Call</button>
        </div> */}
        {/* <div className='fixed bottom-0 left-0 right-0 bg-white border-t flex justify-around py-2'>
          <button
            onClick={() => setActiveTab('details')}
            className={`font-semibold ${
              activeTab === 'details' ? 'text-blue-500' : 'text-gray-500'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('coupons')}
            className={`font-semibold ${
              activeTab === 'coupons' ? 'text-blue-500' : 'text-gray-500'
            }`}
          >
            Coupons
          </button>
          <button
            onClick={() => setActiveTab('stamps')}
            className={`font-semibold ${
              activeTab === 'stamps' ? 'text-blue-500' : 'text-gray-500'
            }`}
          >
            Stamps
          </button>
        </div> */}
        {activeTab === 'details' && (
          <>
            <div className='mt-6'>
              <h2 className='font-bold mb-2'>Restaurant Information:</h2>
              <p className='text-gray-600 mb-2'>{restaurant.description}</p>
              <h2 className='font-bold mb-2'>Categories:</h2>

              <p className='text-gray-600 mb-2'>{restaurant.category}</p>
              <h2 className='font-bold mb-2'>Address:</h2>

              <p className='text-gray-600 mb-2'>{restaurant.address}</p>
              <h2 className='font-bold mb-2'>Phone Number:</h2>

              <p className='text-gray-600 mb-2'>{restaurant.phoneNumber}</p>
              <h2 className='font-bold mb-2'>Open Hours:</h2>
              <p className='text-gray-600 mb-4'>M W T 7:00am - 10:00pm</p>

              {/* <button
                onClick={() => setShowListDialog(true)}
                className='bg-blue-500 text-white py-2 px-4 rounded-lg font-bold'
              >
                
                View Menu
              </button> */}
              <h2 className='font-bold mb-2'>Menu:</h2>
            </div>
          </>
        )}
        {/* {activeTab === 'coupons' && (
          <div className='mt-6'>
            <h2 className='font-bold mb-2'>Coupons</h2>
            <p>No coupons available at the moment.</p>
          </div>
        )} */}
        {activeTab === 'coupons' && (
          <div className='mt-6  p-6 rounded-lg'>
            {/* <h2 className='text-2xl font-bold mb-6 text-gray-800'>
              My Active Coupons
            </h2> */}
            {isSubscribed ? (
              <>
                <div className='mb-6 flex flex-col sm:flex-row w-full space-y-4 sm:space-y-0 sm:space-x-4'>
                  <div className='relative flex-grow'>
                    <input
                      type='text'
                      placeholder='Search by code or content...'
                      value={filters.searchTerm}
                      onChange={handleSearchChange}
                      className='w-full p-3 border border-gray-300 rounded-3xl pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    />
                    <FaSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
                  </div>
                  <button
                    onClick={() => handleSortChange('expirationDate')}
                    className='flex items-center px-4 py-2 bg-white border max-w-[200px] border-gray-300 rounded-3xl shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-150 ease-in-out'
                  >
                    Sort by Expiration{' '}
                    {filters.sortField === 'expirationDate' &&
                      (filters.sortDirection === 'asc' ? (
                        <FaArrowUp className='inline ml-1' />
                      ) : (
                        <FaArrowDown className='inline ml-1' />
                      ))}
                  </button>
                </div>

                {/* {isSubscribed ? (
              loadingStamps ? (
                <div className='flex justify-center items-center h-64'>
                  <FaSpinner className='animate-spin text-4xl text-blue-500' />
                </div>
              ) : ( */}
                {loadingCoupons ? (
                  <div className='flex justify-center items-center h-64'>
                    <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500'></div>
                  </div>
                ) : error ? (
                  <p className='text-red-500 text-center py-4'>{error}</p>
                ) : coupons.length === 0 ? (
                  // <p className='text-gray-500 text-center py-4'>
                  //   No active coupons available.
                  // </p>
                  <div className='text-center py-10'>
                    <FaTicketAlt className='text-6xl text-gray-300 mx-auto mb-4' />
                    <p className='text-xl text-gray-500'>
                      No active coupons available.
                    </p>
                    <p className='text-gray-400 mt-2'>
                      Check back later for new offers!
                    </p>
                  </div>
                ) : (
                  <>
                    <ul className='space-y-4'>
                      {coupons.map((coupon) => (
                        <li
                          key={coupon._id}
                          // className='bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1'
                          className='border rounded-lg p-4 shadow-sm transition duration-300 ease-in-out transform hover:-translate-y-1'
                        >
                          <Link
                            to={`/redeem/${coupon.code}`}
                            // className='block bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1 cursor-pointer'
                          >
                            <div className='flex justify-between items-start mb-4'>
                              <h3 className='font-semibold text-lg text-blue-600'>
                                {coupon.messageContent}
                              </h3>
                              {/* <span className='px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full'>
                        Active
                      </span> */}
                            </div>
                            <p className='text-gray-600'>Code: {coupon.code}</p>
                            {/* <div className='flex justify-between items-center text-sm text-gray-500'> */}
                            <p>
                              Expires:{' '}
                              {/* {new Date(
                                coupon.expirationDate
                              ).toLocaleDateString()} */}
                              {coupon.expirationDate
                                ? new Date(
                                    coupon.expirationDate
                                  ).toLocaleDateString()
                                : 'No expiration date'}
                            </p>
                            {/* <button className='text-blue-500 hover:text-blue-700 focus:outline-none'>
                        View Details
                      </button> */}
                            {/* </div> */}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className='mt-8'>
                      <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={filters.couponsPerPage}
                        totalItems={pagination.totalCoupons}
                      />
                    </div>

                    <div className='flex justify-between items-center mt-4 text-sm text-gray-600'>
                      {/* <div className='flex flex-col sm:flex-row justify-between items-center mt-6 text-sm text-gray-600'> */}
                      <div>
                        <span className='mr-2'>Coupons per page:</span>
                        <select
                          value={filters.couponsPerPage}
                          onChange={handleCouponsPerPageChange}
                          className='p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        >
                          <option value='6'>6</option>
                          <option value='12'>12</option>
                          <option value='18'>18</option>
                          <option value='30'>30</option>
                        </select>
                      </div>
                      <div>
                        Showing{' '}
                        {(pagination.currentPage - 1) * filters.couponsPerPage +
                          1}{' '}
                        -{' '}
                        {Math.min(
                          pagination.currentPage * filters.couponsPerPage,
                          pagination.totalCoupons
                        )}{' '}
                        of {pagination.totalCoupons} coupons
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className='text-center py-8'>
                <p className='text-lg text-gray-600 mb-4'>
                  You need to be subscribed to view coupons.
                </p>
                {/* <button
                  onClick={handleSubscribe}
                  className='bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition duration-300'
                >
                  Subscribe Now
                </button> */}
              </div>
            )}
          </div>
        )}
        {/* {activeTab === 'coupons' && (
          <div className='mt-6'>
            {coupons.length === 0 ? (
              <p>No coupons available at the moment.</p>
            ) : (
              <ul className='space-y-4'>
                {coupons.map((coupon) => (
                  <li
                    key={coupon._id}
                    className='border rounded-lg p-4 shadow-sm'
                  >
                    <p className='font-semibold text-sky-500'>
                      {coupon.messageContent}
                    </p>
                    <p className='text-sm text-gray-600'>Code: {coupon.code}</p>
                    <p className='text-sm text-gray-600'>
                      Expires:{' '}
                      {new Date(coupon.expirationDate).toLocaleDateString()}
                    </p>
                    <p className='text-sm text-gray-600'>
                      Status: {coupon.isRedeemed ? 'Redeemed' : 'Not Redeemed'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )} */}
        {activeTab === 'stamps' && (
          <div className='mt-6 p-6 rounded-lg'>
            {isSubscribed ? (
              loadingStamps ? (
                <div className='flex justify-center items-center h-64'>
                  <FaSpinner className='animate-spin text-4xl text-blue-500' />
                </div>
              ) : (
                <>
                  <p className='text-lg mb-4'>
                    Your total stamps:{' '}
                    <span className='font-bold'>{customerStamps}</span>
                  </p>

                  {Array.isArray(stampRewards) && stampRewards.length > 0 ? (
                    <ul className='space-y-4'>
                      {stampRewards.map((reward) => (
                        <li
                          key={reward._id}
                          className='bg-white rounded-xl p-6 shadow-md'
                        >
                          <h3 className='text-lg font-semibold mb-2'>
                            {reward.description}
                          </h3>
                          <p className='text-gray-600 mb-4'>
                            Stamps required: {reward.stampCost}
                          </p>
                          <button
                            onClick={() => handleRedeemReward(reward._id)}
                            disabled={customerStamps < reward.stampCost}
                            className={`px-4 py-2 rounded ${
                              customerStamps >= reward.stampCost
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            Redeem
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    // <p>No rewards available at the moment.</p>
                    <div className='text-center py-10'>
                      <FaStamp className='text-6xl text-gray-300 mx-auto mb-4' />
                      <p className='text-xl text-gray-500'>
                        No stamp rewards available.
                      </p>
                      <p className='text-gray-400 mt-2'>
                        Check back later for new rewards!
                      </p>
                    </div>
                  )}
                </>
              )
            ) : (
              <div className='text-center py-8'>
                <p className='text-lg text-gray-600 mb-4'>
                  You need to be subscribed to view stamps and rewards.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {/* <div className='fixed bottom-0 left-0 right-0 bg-white border-t flex justify-around py-2'>
        <button
          onClick={() => setActiveTab('details')}
          className={`font-semibold ${
            activeTab === 'details' ? 'text-blue-500' : 'text-gray-500'
          }`}
        >
          Details
        </button>
        <button
          onClick={() => setActiveTab('coupons')}
          className={`font-semibold ${
            activeTab === 'coupons' ? 'text-blue-500' : 'text-gray-500'
          }`}
        >
          Coupons
        </button>
        <button
          onClick={() => setActiveTab('stamps')}
          className={`font-semibold ${
            activeTab === 'stamps' ? 'text-blue-500' : 'text-gray-500'
          }`}
        >
          Stamps
        </button>
      </div> */}
      {/* 
      {showListDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='bg-white p-6 rounded-lg w-96 h-[500px] overflow-y-auto '
            ref={dialogRef}
          >
            <h2 className='text-xl font-bold mb-4'>Add to Playlist</h2>
            <div className='mb-4'>
              <input
                type='text'
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder='Create new list'
                className='w-full p-2 border rounded'
              />
              <button
                onClick={handleCreateNewList}
                className='mt-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full'
              >
                Create New List
              </button>
            </div>
            <ul>
              {lists.map((list) => (
                <li
                  key={list._id}
                  onClick={() => toggleListSelection(list._id)}
                  className='border-b-2 border-gray-50 cursor-pointer hover:bg-gray-100 p-2 rounded flex justify-between items-center'
                >
                  {list.name}
                 
                  <FaCheck
                    className={`${
                      selectedLists.includes(list._id)
                        ? 'text-green-500'
                        : 'text-gray-500'
                    }`}
                  />
                </li>
              ))}
            </ul>
            <button
              onClick={handleAddToLists}
              className='mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full'
            >
              Done
            </button>
            <button
              onClick={() => setShowListDialog(false)}
              className='mt-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded w-full'
            >
              Cancel
            </button>
          </div>
        </div>
      )} */}

      {showListDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='bg-white p-6 rounded-lg w-96 h-[500px] overflow-y-auto'
            ref={dialogRef}
          >
            <h2 className='text-xl font-bold mb-4'>Add to Playlist</h2>
            <div className='mb-4'>
              <input
                type='text'
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder='Create new list'
                className='w-full p-2 border rounded'
              />
              <button
                onClick={handleCreateNewList}
                className='mt-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full'
              >
                Create New List
              </button>
            </div>
            <ul>
              {lists.map((list) => (
                <li
                  key={list._id}
                  onClick={() => toggleListSelection(list._id)}
                  className='border-b-2 border-gray-50 cursor-pointer hover:bg-gray-100 p-2 rounded flex justify-between items-center'
                >
                  {list.name}
                  <FaCheck
                    className={`${
                      selectedLists.includes(list._id)
                        ? 'text-green-500'
                        : 'text-gray-300'
                    }`}
                  />
                </li>
              ))}
            </ul>
            {/* <button
              onClick={handleAddToLists}
              className='mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full'
            >
              Done
            </button> */}
            <button
              onClick={handleAddToLists}
              disabled={isAddingToList}
              className={`mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full flex items-center justify-center ${
                isAddingToList ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isAddingToList ? (
                <>
                  <FaSpinner className='animate-spin mr-2' />
                  Processing...
                </>
              ) : (
                'Done'
              )}
            </button>
            {/* <button
              onClick={() => {
                setShowListDialog(false);
                setSelectedLists([]);
              }}
              className='mt-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded w-full'
            >
              Cancel
            </button> */}
            <button
              onClick={() => {
                setShowListDialog(false);
                setSelectedLists(restaurantLists);
              }}
              className='mt-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded w-full'
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* {showLoginDialog && (
        <LoginDialog
          onClose={() => setShowLoginDialog(false)}
          message='Please log in or sign up to access this feature.'
        />
      )} */}
      {showLoginDialog && (
        <LoginDialog
          onClose={() => setShowLoginDialog(false)}
          onLogin={handleLogin}
        />
      )}
    </div>
  );
};
// {
/* {showListDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div ref={dialogRef} className='bg-gray-900 p-6 rounded-lg w-80'>
            <h2 className='text-xl font-bold mb-4 text-white'>
              Add To Playlist
            </h2>
            <div className='mb-4'>
              <div className='flex items-center bg-gray-800 rounded-md p-2'>
                <FaPlus className='text-orange-500 mr-2' />
                <input
                  type='text'
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder='Create New Playlist'
                  className='bg-transparent text-white w-full focus:outline-none'
                />
              </div>
            </div>
            <ul className='space-y-2'>
              {lists.map((list) => (
                <li
                  key={list._id}
                  onClick={() => toggleListSelection(list._id)}
                  className='cursor-pointer hover:bg-gray-800 p-2 rounded flex justify-between items-center'
                >
                  <span className='text-white'>{list.name}</span>
                  <FaCheck
                    className={`${
                      selectedLists.includes(list._id)
                        ? 'text-green-500'
                        : 'text-gray-500'
                    }`}
                  />
                </li>
              ))}
            </ul>
            <div className='mt-4 flex justify-between'>
              <button
                onClick={() => setShowListDialog(false)}
                className='text-orange-500 font-bold'
              >
                Cancel
              </button>
              <button
                onClick={handleAddToLists}
                className='bg-orange-500 text-white font-bold py-2 px-4 rounded'
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )} */
// }

// {
/* {error && <p className='text-red-500 mt-4'>{error}</p>} */
// }
//     </div>
//   );
// };

export default RestaurantDetailsPage;
// // components/RestaurantDetails.js
// import React, { useState, useEffect } from 'react';
// import { useParams, Link } from 'react-router-dom';
// import axios from 'axios';
// import { FaHeart, FaRegHeart, FaPlus } from 'react-icons/fa';
// import { useAuth } from '../auth/context/AuthContext';

// const RestaurantDetailsPage = () => {
//   const { restaurantId } = useParams();
//   const { isCustomerAuthenticated, customerToken } = useAuth();
//   const [restaurant, setRestaurant] = useState(null);
//   const [isSubscribed, setIsSubscribed] = useState(false);
//   const [isFavorite, setIsFavorite] = useState(false);
//   const [showListDialog, setShowListDialog] = useState(false);
//   const [lists, setLists] = useState([]);
//   const [newListName, setNewListName] = useState('');
//   const [error, setError] = useState('');

//   useEffect(() => {
//     fetchRestaurantDetails();
//     if (isCustomerAuthenticated) {
//       fetchSubscriptionStatus();
//       fetchFavoriteStatus();
//       fetchLists();
//     }
//   }, [restaurantId, isCustomerAuthenticated]);

//   const fetchRestaurantDetails = async () => {
//     try {
//       const response = await axios.get(`/api/restaurants/${restaurantId}`);
//       setRestaurant(response.data);
//     } catch (error) {
//       setError('Failed to load restaurant details.');
//     }
//   };

//   const fetchSubscriptionStatus = async () => {
//     try {
//       const response = await axios.get(
//         `/api/customers/subscription/${restaurantId}`,
//         {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         }
//       );
//       setIsSubscribed(response.data.isSubscribed);
//     } catch (error) {
//       console.error('Error fetching subscription status:', error);
//     }
//   };

//   const fetchFavoriteStatus = async () => {
//     try {
//       const response = await axios.get(
//         `/api/customers/favorite/${restaurantId}`,
//         {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         }
//       );
//       setIsFavorite(response.data.isFavorite);
//     } catch (error) {
//       console.error('Error fetching favorite status:', error);
//     }
//   };

//   const fetchLists = async () => {
//     try {
//       const response = await axios.get('/api/customers/lists', {
//         headers: { Authorization: `Bearer ${customerToken}` },
//       });
//       setLists(response.data);
//     } catch (error) {
//       console.error('Error fetching lists:', error);
//     }
//   };

//   const handleSubscribe = async () => {
//     try {
//       const response = await axios.post(
//         `/api/restaurants/${restaurantId}/subscribe`,
//         {},
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );
//       if (response.data.success) {
//         setIsSubscribed(true);
//       }
//     } catch (error) {
//       setError('Failed to subscribe. Please try again.');
//     }
//   };

//   const handleFavorite = async () => {
//     try {
//       if (isFavorite) {
//         await axios.delete(`/api/customers/favorite/${restaurantId}`, {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         });
//       } else {
//         await axios.post(
//           `/api/customers/favorite/${restaurantId}`,
//           {},
//           { headers: { Authorization: `Bearer ${customerToken}` } }
//         );
//       }
//       setIsFavorite(!isFavorite);
//     } catch (error) {
//       setError('Failed to update favorite status. Please try again.');
//     }
//   };

//   const handleAddToList = async (listId) => {
//     try {
//       await axios.post(
//         `/api/customers/lists/${listId}/restaurants/${restaurantId}`,
//         {},
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );
//       setShowListDialog(false);
//     } catch (error) {
//       setError('Failed to add restaurant to the list. Please try again.');
//     }
//   };

//   const handleCreateNewList = async () => {
//     try {
//       const response = await axios.post(
//         '/api/customers/lists',
//         { name: newListName },
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );
//       setLists([...lists, response.data]);
//       setNewListName('');
//     } catch (error) {
//       setError('Failed to create new list. Please try again.');
//     }
//   };

//   if (!restaurant) {
//     return <div>Loading...</div>;
//   }

//   return (
//     <div className='max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-xl'>
//       <div className='flex items-center justify-between mb-6'>
//         <h1 className='text-3xl font-bold'>{restaurant.name}</h1>
//         <div className='flex items-center'>
//           {isCustomerAuthenticated && (
//             <button
//               onClick={() => setShowListDialog(true)}
//               className='mr-4 text-gray-600 hover:text-gray-800'
//             >
//               <FaPlus size={24} />
//             </button>
//           )}
//           <button
//             onClick={handleFavorite}
//             className={`text-red-500 ${
//               isFavorite ? 'hover:text-red-700' : 'hover:text-red-600'
//             }`}
//           >
//             {isFavorite ? <FaHeart size={24} /> : <FaRegHeart size={24} />}
//           </button>
//         </div>
//       </div>

//       <div className='flex mb-6'>
//         <div
//           className='w-32 h-32 rounded-full bg-gray-300 flex items-center justify-center text-4xl font-bold text-white'
//           style={{
//             backgroundColor: restaurant.profileImage
//               ? 'transparent'
//               : `#${Math.floor(Math.random() * 16777215).toString(16)}`,
//           }}
//         >
//           {restaurant.profileImage ? (
//             <img
//               src={restaurant.profileImage}
//               alt={restaurant.name}
//               className='w-full h-full object-cover rounded-full'
//             />
//           ) : (
//             restaurant.name.charAt(0).toUpperCase()
//           )}
//         </div>
//         <div className='ml-6'>
//           <p className='text-gray-600 mb-2'>{restaurant.address}</p>
//           <p className='text-gray-800'>{restaurant.description}</p>
//         </div>
//       </div>

//       <div className='flex justify-between items-center mb-6'>
//         <p className='text-gray-600'>
//           {restaurant.subscribersCount} subscribers
//         </p>
//         {isCustomerAuthenticated &&
//           (isSubscribed ? (
//             <span className='text-green-500 font-semibold'>Subscribed</span>
//           ) : (
//             <button
//               onClick={handleSubscribe}
//               className='bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded'
//             >
//               Subscribe
//             </button>
//           ))}
//       </div>

//       {isCustomerAuthenticated && (
//         <div className='mt-8'>
//           <Link
//             to={`/restaurant/${restaurantId}/coupons`}
//             className='text-blue-500 hover:text-blue-700 mr-6'
//           >
//             My Coupons from this Restaurant
//           </Link>
//           <Link
//             to={`/restaurant/${restaurantId}/rewards`}
//             className='text-blue-500 hover:text-blue-700'
//           >
//             Rewards
//           </Link>
//         </div>
//       )}

//       {showListDialog && (
//         <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center'>
//           <div className='bg-white p-6 rounded-lg w-96'>
//             <h2 className='text-xl font-bold mb-4'>Add to List</h2>
//             <div className='mb-4'>
//               <input
//                 type='text'
//                 value={newListName}
//                 onChange={(e) => setNewListName(e.target.value)}
//                 placeholder='Create new list'
//                 className='w-full p-2 border rounded'
//               />
//               <button
//                 onClick={handleCreateNewList}
//                 className='mt-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full'
//               >
//                 Create New List
//               </button>
//             </div>
//             <ul>
//               {lists.map((list) => (
//                 <li
//                   key={list._id}
//                   onClick={() => handleAddToList(list._id)}
//                   className='cursor-pointer hover:bg-gray-100 p-2 rounded'
//                 >
//                   {list.name}
//                 </li>
//               ))}
//             </ul>
//             <button
//               onClick={() => setShowListDialog(false)}
//               className='mt-4 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded w-full'
//             >
//               Cancel
//             </button>
//           </div>
//         </div>
//       )}

//       {error && <p className='text-red-500 mt-4'>{error}</p>}
//     </div>
//   );
// };

// export default RestaurantDetailsPage;
// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import axios from 'axios';

// const RestaurantDetails = () => {
//   const { restaurantId } = useParams();
//   const navigate = useNavigate();
//   const [restaurant, setRestaurant] = useState(null);
//   const [subscriberCount, setSubscriberCount] = useState(0);
//   const [isSubscribed, setIsSubscribed] = useState(false);
//   const [token, setToken] = useState(localStorage.getItem('customerToken'));
//   const [lists, setLists] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [selectedList, setSelectedList] = useState('');
//   const [isProcessingSave, setIsProcessingSave] = useState(false);

//   useEffect(() => {
//     // const fetchRestaurantDetails = async () => {
//     //   try {
//     //     const response = await axios.get(`/api/restaurants/${restaurantId}`);
//     //     setRestaurant(response.data.restaurant);
//     //     setSubscriberCount(response.data.subscriberCount);
//     //   } catch (error) {
//     //     console.error('Error fetching restaurant details:', error);
//     //   }
//     // };

//     const fetchRestaurantDetails = async () => {
//       try {
//         //   if (customerId) {
//         //     console.log(customerId);
//         //   }
//         const response = await axios.get(`/api/restaurants/${restaurantId}`);
//         console.log(response);
//         setRestaurant(response.data);
//       } catch (error) {
//         //   setError('Failed to load restaurant information.');
//         //   toast.error('Failed to fetch restaurant details');
//       } finally {
//         //   setLoading(false);
//       }
//     };

//     const checkSubscriptionStatus = async () => {
//       if (token) {
//         try {
//           const response = await axios.get(
//             `/api/restaurants/${restaurantId}/subscription-status`,
//             {
//               headers: { Authorization: `Bearer ${token}` },
//             }
//           );
//           setIsSubscribed(response.data.isSubscribed);
//         } catch (error) {
//           console.error('Error checking subscription status:', error);
//         }
//       }
//     };

//     fetchRestaurantDetails();
//     checkSubscriptionStatus();
//   }, [restaurantId, token]);

//   const handleSaveFavorite = async () => {
//     try {
//       const response = await axios.get(
//         `/api/restaurants/${restaurantId}/favorite-lists`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setLists(response.data.lists);
//       setShowModal(true);
//     } catch (error) {
//       console.error('Error fetching favorite lists:', error);
//     }
//   };

//   const handleSaveToList = async () => {
//     setIsProcessingSave(true);
//     try {
//       await axios.post(
//         `/api/restaurants/${restaurantId}/save`,
//         { listId: selectedList },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setShowModal(false);
//     } catch (error) {
//       console.error('Error saving to list:', error);
//     } finally {
//       setIsProcessingSave(false);
//     }
//   };

//   const handleSubscribe = async () => {
//     try {
//       await axios.post(
//         `/api/restaurants/${restaurantId}/subscribe`,
//         {},
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setIsSubscribed(true);
//     } catch (error) {
//       console.error('Error subscribing:', error);
//     }
//   };

//   return (
//     <div className='max-w-screen-md mx-auto p-4'>
//       {restaurant && (
//         <>
//           {/* Header with Back and Share buttons */}
//           <div className='flex justify-between items-center mb-4'>
//             <button
//               className='text-gray-600 text-lg'
//               onClick={() => navigate(-1)}
//             >
//               ← Back
//             </button>
//             <button className='text-gray-600 text-lg'>Share</button>
//           </div>

//           {/* Restaurant Image */}
//           <div className='mb-4 relative'>
//             <img
//               src={restaurant.profileImage}
//               alt={restaurant.name}
//               className='w-full h-48 object-cover rounded-lg'
//             />
//             <div className='absolute top-4 right-4 bg-white p-2 rounded-full shadow'>
//               <span className='text-sm font-semibold'>{restaurant.qrCode}</span>
//             </div>
//           </div>

//           {/* Restaurant Information */}
//           <div className='mb-4'>
//             <h1 className='text-2xl font-bold mb-2'>{restaurant.name}</h1>
//             <p className='text-gray-600 mb-2'>{restaurant.address}</p>
//             <p className='text-gray-600 mb-4'>{restaurant.description}</p>

//             {/* Show Subscribers Count */}
//             <div className='flex items-center mb-4'>
//               <span className='text-sm font-medium text-gray-600'>
//                 {subscriberCount} Subscribers
//               </span>
//             </div>

//             {/* Subscription and Actions */}
//             {isSubscribed ? (
//               <p className='text-green-600 font-semibold'>
//                 You are already subscribed
//               </p>
//             ) : (
//               <button
//                 onClick={handleSubscribe}
//                 className='w-full py-2 px-4 bg-red-600 text-white rounded-md shadow-md hover:bg-red-700'
//               >
//                 Subscribe
//               </button>
//             )}

//             {/* Favorite Button */}
//             <button
//               onClick={handleSaveFavorite}
//               className='w-full py-2 px-4 mt-4 bg-gray-600 text-white rounded-md shadow-md hover:bg-gray-700'
//             >
//               Save to Favorites
//             </button>

//             {/* Coupons and Rewards Links */}
//             {token && (
//               <>
//                 <button
//                   onClick={() =>
//                     navigate(`/restaurants/${restaurantId}/coupons`)
//                   }
//                   className='w-full py-2 px-4 mt-4 bg-indigo-500 text-white rounded-md shadow-md hover:bg-indigo-600'
//                 >
//                   My Coupons
//                 </button>
//                 <button
//                   onClick={() =>
//                     navigate(`/restaurants/${restaurantId}/rewards`)
//                   }
//                   className='w-full py-2 px-4 mt-4 bg-green-500 text-white rounded-md shadow-md hover:bg-green-600'
//                 >
//                   View Rewards
//                 </button>
//               </>
//             )}
//           </div>

//           {/* Modal for Save to List */}
//           {showModal && (
//             <div className='fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-75'>
//               <div className='bg-white rounded-lg p-6 w-80'>
//                 <h2 className='text-xl font-semibold mb-4'>Save to List</h2>
//                 <ul className='mb-4'>
//                   <li className='flex items-center'>
//                     <input
//                       type='radio'
//                       id='favorites'
//                       name='list'
//                       value='favorites'
//                       className='mr-2'
//                     />
//                     <label htmlFor='favorites'>My Favorites</label>
//                   </li>
//                   <li className='flex items-center'>
//                     <input
//                       type='radio'
//                       id='new-list'
//                       name='list'
//                       value='new-list'
//                       className='mr-2'
//                     />
//                     <label htmlFor='new-list'>Create New List</label>
//                   </li>
//                 </ul>
//                 <button
//                   onClick={handleSaveToList}
//                   className='w-full py-2 px-4 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700'
//                   disabled={isProcessingSave}
//                 >
//                   {isProcessingSave ? 'Saving...' : 'Save'}
//                 </button>
//               </div>
//             </div>
//           )}
//         </>
//       )}
//     </div>
//   );
// };

// export default RestaurantDetails;

// // // components/RestaurantDetailsPage.js
// // import React, { useState, useEffect } from 'react';
// // import axios from 'axios';
// // import { useParams, Link } from 'react-router-dom';
// // import { toast } from 'react-toastify';

// // const RestaurantDetailsPage = () => {
// //   const { restaurantId, customerId } = useParams();
// //   const [restaurant, setRestaurant] = useState(null);
// //   const [loading, setLoading] = useState(true);

// //   useEffect(() => {
// //     fetchRestaurantDetails();
// //   }, [restaurantId]);
// //   const fetchRestaurantDetails = async () => {
// //     try {
// //       if (customerId) {
// //         console.log(customerId);
// //       }
// //       const response = await axios.get(`/api/restaurants/${restaurantId}`);
// //       console.log(response);
// //       setRestaurant(response.data);
// //     } catch (error) {
// //       //   setError('Failed to load restaurant information.');
// //       toast.error('Failed to fetch restaurant details');
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   //   const fetchRestaurantDetails = async () => {
// //   //     try {
// //   //       setLoading(true);
// //   //       const response = await axios.get(
// //   //         `/api/customer/restaurant/${restaurantId}`,
// //   //         {
// //   //           headers: {
// //   //             Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
// //   //           },
// //   //         }
// //   //       );
// //   //       setRestaurant(response.data);
// //   //     } catch (error) {
// //   //       toast.error('Failed to fetch restaurant details');
// //   //     } finally {
// //   //       setLoading(false);
// //   //     }
// //   //   };

// //   if (loading) {
// //     return <div>Loading...</div>;
// //   }

// //   if (!restaurant) {
// //     return <div>Restaurant not found</div>;
// //   }

// //   return (
// //     <div className='container mx-auto px-4 py-8'>
// //       <h1 className='text-3xl font-bold mb-6'>{restaurant.name}</h1>
// //       <p className='mb-4'>{restaurant.description}</p>
// //       <div className='flex space-x-4'>
// //         <Link
// //           to={`/customer/restaurant/${restaurantId}/coupons`}
// //           className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded'
// //         >
// //           My Coupons
// //         </Link>
// //         {restaurant.stampSettings.enabled && (
// //           <Link
// //             to={`/customer/restaurant/${restaurantId}/rewards`}
// //             className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
// //           >
// //             Rewards
// //           </Link>
// //         )}
// //       </div>
// //     </div>
// //   );
// // };

// // export default RestaurantDetailsPage;
